import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Mic, Sparkles, Activity, Compass, Target, RotateCcw, Lock } from 'lucide-react';
import { useVoiceContour } from '../lib/voiceContour';

const VERDICTS = {
  flat: {
    title: 'Mostly flat',
    body: (st) => `Your pitch moved about ${st} semitones. A flat melody makes listeners tune out even when every sound is right — and it’s one of the fastest things to fix.`,
  },
  moderate: {
    title: 'Some melody',
    body: (st) => `Your pitch moved about ${st} semitones. Lift the key words and let your sentence endings fall, and you’ll sound noticeably more sure of yourself.`,
  },
  expressive: {
    title: 'Expressive',
    body: (st) => `Your pitch moved about ${st} semitones — your voice is alive. Next step: make every sound land just as clearly.`,
  },
  unclear: {
    title: 'We couldn’t hear enough',
    body: () => 'Say a full sentence or two, a little closer to the mic, and try again.',
  },
};

const WarmUp = () => {
  const contour = useVoiceContour({ maxSeconds: 8 });
  const { status, readout, summary } = contour;
  const live = status === 'live';
  const v = summary && VERDICTS[summary.verdict];

  return (
    <div className="cz-card overflow-hidden" data-testid="warmup">
      <div className="relative">
        <canvas ref={contour.canvasRef} className="block h-48 w-full sm:h-64" aria-hidden="true" />
        {live && (
          <div className="absolute left-5 top-4 flex items-center gap-2 text-xs font-medium text-white/70">
            <span className="h-2 w-2 animate-pulse rounded-full bg-coral-500" />
            Listening · {Math.max(0, Math.ceil(8 - readout.seconds))}s
          </div>
        )}
        {live && (
          <div className="absolute right-5 top-4 font-mono text-xs tabular-nums text-white/50">
            {readout.hz ? `${readout.hz} Hz` : '— Hz'}
          </div>
        )}
      </div>

      <div className="border-t border-white/[0.06] px-5 py-5 sm:px-7">
        {status === 'done' && v ? (
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between animate-fade-up">
            <div className="max-w-xl">
              <p className="cz-eyebrow">Your melody range</p>
              <p className="mt-1 text-2xl font-semibold tracking-tight text-white">
                {v.title}
                {summary.verdict !== 'unclear' && <span className="ml-2 text-white/40">· {summary.rangeSt} st</span>}
              </p>
              <p className="mt-1.5 text-sm leading-relaxed text-white/60">{v.body(summary.rangeSt)}</p>
            </div>
            <div className="flex shrink-0 gap-2">
              <button type="button" onClick={() => contour.start()} className="cz-btn-ghost !px-4">
                <RotateCcw className="h-4 w-4" /> Again
              </button>
              <Link to="/practice" className="cz-btn-primary !px-5">
                Train it <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <p className="flex items-start gap-2 text-sm text-white/55">
              <Lock className="mt-0.5 h-4 w-4 shrink-0 text-white/35" />
              {status === 'denied'
                ? 'Microphone access was blocked. Allow it in your browser settings to try the warm-up.'
                : status === 'unsupported'
                ? 'This browser can’t open the microphone. Try the latest Chrome or Safari.'
                : 'Runs entirely in your browser. Nothing is recorded or uploaded.'}
            </p>
            <button
              type="button"
              onClick={live ? contour.stop : () => contour.start()}
              disabled={status === 'requesting'}
              className={live ? 'cz-btn-ghost shrink-0' : 'cz-btn-primary shrink-0'}
              data-testid="warmup-btn"
            >
              {live ? 'Finish' : <><Mic className="h-4 w-4" /> Start the 8-second warm-up</>}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const ACTIONS = [
  {
    to: '/accent-test',
    icon: Sparkles,
    eyebrow: '30 seconds',
    title: 'I bet I can guess your accent.',
    body: 'Talk about anything for half a minute. Our AI names your accent and the exact sounds that give it away.',
    cta: 'Guess my accent',
    accent: 'from-coral-500/20',
  },
  {
    to: '/practice',
    icon: Mic,
    eyebrow: '2 minutes',
    title: 'Practice a sentence.',
    body: 'Read a line out loud and get every word — and every sound inside it — scored, with what to fix first.',
    cta: 'Start practicing',
    accent: 'from-brand-500/25',
  },
];

const PILLARS = [
  { icon: Activity, title: 'Measure', body: 'Phoneme-level scores for pronunciation, fluency and prosody — numbers, not vibes.' },
  { icon: Compass, title: 'Know', body: 'Your first language, your weak sounds and your level, remembered between sessions.' },
  { icon: Target, title: 'Coach', body: 'Short daily drills built on how the best speech coaches train their clients.' },
];

const Home = () => (
  <div className="mx-auto max-w-6xl px-5">
    <section className="pt-10 sm:pt-16">
      <div className="max-w-3xl animate-fade-up">
        <p className="cz-eyebrow">Your speech coach</p>
        <h1 className="mt-4 text-[2.6rem] font-semibold leading-[1.02] tracking-[-0.035em] text-white sm:text-6xl">
          Every voice has a melody.{' '}
          <span className="cz-display text-[1.08em] text-transparent bg-clip-text bg-gradient-to-r from-brand-400 to-aqua-400">See yours.</span>
        </h1>
        <p className="mt-5 max-w-xl text-lg leading-relaxed text-white/60">
          Say anything and Cadenza draws the melody of your English, live. Most people sound flatter than they think —
          find out in eight seconds.
        </p>
      </div>
      <div className="mt-8 animate-fade-up [animation-delay:120ms]">
        <WarmUp />
      </div>
    </section>

    <section className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
      {ACTIONS.map(({ to, icon: Icon, eyebrow, title, body, cta, accent }) => (
        <Link
          key={to}
          to={to}
          className={`group cz-card relative overflow-hidden bg-gradient-to-br ${accent} to-transparent p-6 transition-colors hover:border-white/15 sm:p-8`}
        >
          <div className="flex items-center justify-between">
            <span className="grid h-11 w-11 place-items-center rounded-2xl bg-white/[0.06] text-white">
              <Icon className="h-5 w-5" />
            </span>
            <span className="text-xs font-medium text-white/40">{eyebrow}</span>
          </div>
          <h2 className="mt-6 text-2xl font-semibold tracking-tight text-white">{title}</h2>
          <p className="mt-2 text-[15px] leading-relaxed text-white/60">{body}</p>
          <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-white">
            {cta} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </span>
        </Link>
      ))}
    </section>

    <section className="mt-16">
      <p className="cz-eyebrow">How Cadenza works</p>
      <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-3">
        {PILLARS.map(({ icon: Icon, title, body }, i) => (
          <div key={title} className="cz-card p-6">
            <div className="flex items-center gap-3">
              <span className="font-mono text-xs text-white/30">0{i + 1}</span>
              <Icon className="h-4 w-4 text-brand-400" />
              <h3 className="font-semibold text-white">{title}</h3>
            </div>
            <p className="mt-3 text-sm leading-relaxed text-white/55">{body}</p>
          </div>
        ))}
      </div>
    </section>
  </div>
);

export default Home;
